/**
 * ScheduleCard Component
 * Displays a single workflow or system task schedule with actions
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Calendar,
  Clock,
  Play,
  MoreVertical,
  Trash2,
  Edit,
  History,
  CheckCircle,
  XCircle,
  Power,
  Workflow,
  Settings,
} from 'lucide-react';
import type { WorkflowSchedule } from '@/types/workflowSchedule';
import type { SystemTaskSchedule, ScheduleCategory } from '@/types/systemTaskSchedule';
import {
  SCHEDULE_TYPE_LABELS,
  SCHEDULE_STATUS_LABELS,
} from '@/types/workflowSchedule';
import { SYSTEM_TASK_TYPE_LABELS } from '@/types/systemTaskSchedule';

type Schedule = WorkflowSchedule | SystemTaskSchedule;

interface ScheduleCardProps {
  schedule: Schedule;
  category: ScheduleCategory;
  onToggle: (id: string, enabled: boolean) => void;
  onRunNow: (id: string) => void;
  onEdit: (schedule: Schedule) => void;
  onDelete: (id: string) => void;
  onViewHistory?: (id: string) => void;
  isLoading?: boolean;
}

// Helper to determine schedule type
function isWorkflowSchedule(schedule: Schedule): schedule is WorkflowSchedule {
  return 'workflow_id' in schedule;
}

// Simple date formatting function
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}-${day} ${hours}:${minutes}`;
}

// Format relative time (simple version)
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (diffMs < 0) {
    return '已过期';
  } else if (diffMins < 60) {
    return `${diffMins} 分钟后`;
  } else if (diffHours < 24) {
    return `${diffHours} 小时后`;
  } else {
    return `${diffDays} 天后`;
  }
}

export function ScheduleCard({
  schedule,
  category,
  onToggle,
  onRunNow,
  onEdit,
  onDelete,
  onViewHistory,
  isLoading,
}: ScheduleCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);

  const getStatusBadge = () => {
    const variants: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-700',
      paused: 'bg-yellow-100 text-yellow-700',
      error: 'bg-red-100 text-red-700',
    };

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${variants[schedule.status] || 'bg-gray-100 text-gray-700'}`}>
        {SCHEDULE_STATUS_LABELS[schedule.status] || schedule.status}
      </span>
    );
  };

  const getCategoryBadge = () => {
    if (category === 'workflow') {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
          <Workflow className="h-3 w-3" />
          工作流
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
        <Settings className="h-3 w-3" />
        系统
      </span>
    );
  };

  const getScheduleDescription = () => {
    const config = schedule.schedule_config;

    if (schedule.schedule_type === 'once') {
      const runAt = config.run_at as string;
      return `一次性: ${formatDate(runAt)}`;
    }

    if (schedule.schedule_type === 'recurring') {
      const interval = config.interval as string;
      const time = config.time as string;
      const intervalLabels: Record<string, string> = {
        hourly: '每小时',
        daily: '每天',
        weekly: '每周',
        monthly: '每月',
      };
      return `${intervalLabels[interval] || interval} ${time}`;
    }

    if (schedule.schedule_type === 'cron') {
      return `Cron: ${config.expression}`;
    }

    return SCHEDULE_TYPE_LABELS[schedule.schedule_type];
  };

  const getSubtitle = () => {
    if (isWorkflowSchedule(schedule)) {
      return schedule.workflow_name ? `工作流: ${schedule.workflow_name}` : null;
    } else {
      const taskLabel = SYSTEM_TASK_TYPE_LABELS[schedule.task_type as keyof typeof SYSTEM_TASK_TYPE_LABELS];
      return `任务类型: ${taskLabel || schedule.task_type}`;
    }
  };

  const successRate =
    schedule.total_runs > 0
      ? Math.round((schedule.successful_runs / schedule.total_runs) * 100)
      : 0;

  return (
    <Card className={!schedule.is_enabled ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {getCategoryBadge()}
            </div>
            <h3 className="text-base font-medium flex items-center gap-2 truncate">
              <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
              {schedule.name}
            </h3>
            {getSubtitle() && (
              <p className="text-sm text-gray-500 truncate">
                {getSubtitle()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2">
            {getStatusBadge()}
            <button
              onClick={() => onToggle(schedule.id, !schedule.is_enabled)}
              disabled={isLoading || schedule.status === 'completed'}
              className={`p-1 rounded ${schedule.is_enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
              title={schedule.is_enabled ? '禁用' : '启用'}
            >
              <Power className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Schedule Description */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <Clock className="h-4 w-4 text-gray-400" />
          <span>{getScheduleDescription()}</span>
        </div>

        {/* Next Run */}
        {schedule.next_run_at && schedule.is_enabled && (
          <div className="text-sm mb-2">
            <span className="text-gray-500">下次执行: </span>
            <span className="font-medium">
              {formatRelativeTime(schedule.next_run_at)}
            </span>
            <span className="text-gray-400 ml-1">
              ({formatDate(schedule.next_run_at)})
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <History className="h-4 w-4" />
            <span>{schedule.total_runs} 次</span>
          </div>
          {schedule.total_runs > 0 && (
            <>
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>{schedule.successful_runs}</span>
              </div>
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="h-4 w-4" />
                <span>{schedule.failed_runs}</span>
              </div>
              <div className="text-gray-500">
                {successRate}%
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t">
          <Button
            variant="secondary"
            onClick={() => setRunConfirmOpen(true)}
            disabled={isLoading || !schedule.is_enabled}
            className="text-sm"
          >
            <Play className="h-4 w-4 mr-1" />
            立即执行
          </Button>

          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded hover:bg-gray-100"
            >
              <MoreVertical className="h-4 w-4 text-gray-500" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                  <button
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit(schedule);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                    编辑
                  </button>
                  {onViewHistory && (
                    <button
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      onClick={() => {
                        setMenuOpen(false);
                        onViewHistory(schedule.id);
                      }}
                    >
                      <History className="h-4 w-4" />
                      执行历史
                    </button>
                  )}
                  <button
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete(schedule.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    删除
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Run confirmation modal */}
        {runConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setRunConfirmOpen(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold mb-2">确认立即执行</h3>
              <p className="text-gray-600 mb-4">
                确定要立即执行任务 "<span className="font-medium">{schedule.name}</span>" 吗？
              </p>
              {category === 'workflow' && (
                <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded mb-4">
                  此操作将立即触发工作流执行，可能会影响目标用户。
                </p>
              )}
              {category === 'system_task' && (
                <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded mb-4">
                  此操作将立即执行系统任务，请确保这是您期望的操作。
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setRunConfirmOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setRunConfirmOpen(false);
                    onRunNow(schedule.id);
                  }}
                  disabled={isLoading}
                >
                  <Play className="h-4 w-4 mr-1" />
                  确认执行
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
